/**
 * The assistant's conversation, kept for the length of the browser tab.
 *
 * The chat runtime lives in the page, so it would lose the thread on every
 * navigation and every reload. This module keeps what the owner has to see
 * again — role and text of each message, plus the context a thread from
 * "Problem melden" is bound to — in `sessionStorage`, one entry per account.
 *
 * Pure functions over a `Storage`; the chat view wires them to the runtime.
 * Every access is guarded: a blocked, full or corrupt storage means an empty
 * thread, never an error.
 */
import type { ThreadMessageLike } from '@assistant-ui/react';
import type { AssistantContext } from '../types/assistant';
import { AWAITING_CONFIRMATION_KEY, isRestoredPreview, pendingConfirmations } from './assistantConfirmation';

export const THREAD_KEY_PREFIX = 'tg.assistant.thread.v1:';
/** Older messages are dropped; the thread stays small and the request history short. */
export const MAX_STORED_MESSAGES = 60;
const STORAGE_VERSION = 1;

export interface StoredMessage {
  role: 'user' | 'assistant';
  text: string;
  /**
   * Set on the last answer only: it ended with a change waiting for the owner's
   * click. Just that fact is kept — never the token or the preview itself — so
   * a restored thread can say the preview is gone instead of pointing at a
   * button that no longer exists.
   */
  awaitingConfirmation?: true;
}

export interface StoredThread {
  messages: StoredMessage[];
  context: AssistantContext | null;
}

/** What a runtime message looks like for this module (assistant-ui's `ThreadMessage`, structurally). */
export interface SerializableMessage {
  readonly role: string;
  readonly content: string | ReadonlyArray<{ readonly type: string; readonly text?: string; readonly result?: unknown }>;
  readonly status?: { readonly type: string };
  /** A restored message carries its "had a change waiting" fact here. */
  readonly metadata?: { readonly custom?: Record<string, unknown> };
}

export const emptyThread = (): StoredThread => ({ messages: [], context: null });

function defaultStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Access itself throws when storage is blocked.
    return null;
  }
}

/** One entry per business account; `null` (nobody known) means nothing is kept. */
export function assistantThreadKey(
  user: { id?: string | null; email?: string | null } | null | undefined,
): string | null {
  const who = user?.id || user?.email?.trim().toLowerCase();
  return who ? `${THREAD_KEY_PREFIX}${who}` : null;
}

function textOf(content: SerializableMessage['content']): string {
  if (typeof content === 'string') return content;
  return content
    .filter((part) => part.type === 'text' && typeof part.text === 'string')
    .map((part) => part.text as string)
    .join('');
}

/**
 * Newest messages only, and never starting on an answer: a thread that opens
 * with the assistant is not one the model can continue.
 */
export function capMessages(messages: readonly StoredMessage[]): StoredMessage[] {
  const tail = messages.slice(-MAX_STORED_MESSAGES);
  const firstUser = tail.findIndex((message) => message.role === 'user');
  return firstUser === -1 ? [] : tail.slice(firstUser);
}

/**
 * Runtime messages → what is stored. Tool steps are not kept, only text; an
 * answer that is still streaming is not final and is left out.
 */
export function serializeMessages(messages: readonly SerializableMessage[]): StoredMessage[] {
  const stored: StoredMessage[] = [];
  let lastWaiting = false;
  for (const message of messages) {
    if (message.role !== 'user' && message.role !== 'assistant') continue;
    if (message.role === 'assistant' && message.status?.type === 'running') continue;
    const text = textOf(message.content);
    if (text.trim() === '') continue;
    stored.push({ role: message.role, text });
    // Waiting now (live steps), or restored from storage as waiting and not answered since.
    lastWaiting =
      message.role === 'assistant' &&
      (isRestoredPreview(message.metadata?.custom) ||
        (typeof message.content !== 'string' && pendingConfirmations(message.content).length > 0));
  }
  const capped = capMessages(stored);
  // Only the newest message can still be answered with a click.
  const last = capped[capped.length - 1];
  return lastWaiting && last?.role === 'assistant' ? [...capped.slice(0, -1), { ...last, awaitingConfirmation: true }] : capped;
}

/** Stored messages → the runtime's `initialMessages`. */
export function toInitialMessages(messages: readonly StoredMessage[]): ThreadMessageLike[] {
  return messages.map((message) => ({
    role: message.role,
    content: [{ type: 'text' as const, text: message.text }],
    ...(message.awaitingConfirmation ? { metadata: { custom: { [AWAITING_CONFIRMATION_KEY]: true } } } : {}),
  }));
}

function parseContext(raw: unknown): AssistantContext | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { conversation_id: id, origin } = raw as Record<string, unknown>;
  const context: AssistantContext = {};
  if (typeof id === 'number' && Number.isInteger(id) && id > 0) context.conversation_id = id;
  if (origin === 'report_problem') context.origin = 'report_problem';
  return Object.keys(context).length > 0 ? context : null;
}

function parseMessages(raw: unknown): StoredMessage[] {
  if (!Array.isArray(raw)) return [];
  const messages: StoredMessage[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { role, text } = entry as Record<string, unknown>;
    if ((role === 'user' || role === 'assistant') && typeof text === 'string' && text.trim() !== '') {
      messages.push({ role, text, ...(role === 'assistant' && entry.awaitingConfirmation === true ? { awaitingConfirmation: true as const } : {}) });
    }
  }
  const capped = capMessages(messages);
  // The flag means "the NEWEST message waits": it holds for the last message only.
  return capped.map((message, index) =>
    message.awaitingConfirmation && index !== capped.length - 1 ? { role: message.role, text: message.text } : message,
  );
}

/** The stored thread; empty when there is none, it belongs to another version or it is damaged. */
export function loadThread(key: string | null, storage: Storage | null = defaultStorage()): StoredThread {
  if (!key || !storage) return emptyThread();
  try {
    const raw = storage.getItem(key);
    if (!raw) return emptyThread();
    const data: unknown = JSON.parse(raw);
    if (typeof data !== 'object' || data === null) return emptyThread();
    const record = data as Record<string, unknown>;
    if (record.v !== STORAGE_VERSION) return emptyThread();
    return { messages: parseMessages(record.messages), context: parseContext(record.context) };
  } catch {
    return emptyThread();
  }
}

/** Keeps the thread; a thread with neither messages nor context is removed instead. */
export function saveThread(
  key: string | null,
  thread: StoredThread,
  storage: Storage | null = defaultStorage(),
): void {
  if (!key || !storage) return;
  try {
    const messages = capMessages(thread.messages);
    if (messages.length === 0 && thread.context === null) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, JSON.stringify({ v: STORAGE_VERSION, messages, context: thread.context }));
  } catch {
    // Full or blocked storage: the thread simply lives on in memory.
  }
}

export function clearThread(key: string | null, storage: Storage | null = defaultStorage()): void {
  saveThread(key, emptyThread(), storage);
}

/** How the owner arrived: "Problem melden" starts a thread of its own, anything else resumes. */
export interface ThreadIntent {
  reportMode: boolean;
  /** The conversation a report is about, when it is already known. */
  conversationId: number | null;
}

/**
 * The thread to open with. A report is always a FRESH thread bound to its
 * context (what was stored before is replaced); otherwise the stored thread
 * continues.
 */
export function startThread(
  key: string | null,
  intent: ThreadIntent,
  storage: Storage | null = defaultStorage(),
): StoredThread {
  if (!intent.reportMode && intent.conversationId === null) return loadThread(key, storage);
  const context: AssistantContext = { origin: 'report_problem' };
  if (intent.conversationId !== null) context.conversation_id = intent.conversationId;
  return { messages: [], context };
}

/** Logout: customer names and numbers in a thread must not outlive the session. */
export function clearAllStoredThreads(storage: Storage | null = defaultStorage()): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(THREAD_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // Nothing to clear when storage cannot be read.
  }
}
